import { dateToLabel } from 'further-ui/utils';
import { InvestorType } from 'further-types/investor';
import useInvestorCertification from 'hooks/data/investor/useInvestorCertification';
import useSkipInvestmentCoolOff from 'hooks/data/investmentCoolOff/useSkipInvestmentCoolOff';
import useUndoSkipInvestmentCoolOff from 'hooks/data/investmentCoolOff/useUndoSkipInvestmentCoolOff';
import useInvestmentCoolOff from 'hooks/data/investmentCoolOff/useInvestmentCoolOff';
import RecordRow from 'components/RecordRow';
import Spacing from 'components/Spacing';
import Button from 'components/Button';
import { Row } from 'components/Layout';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  investorId: string;
  firmId?: string; // NB. Should be required, but the Investor schema isnot updated yet
};

const CoolOffStatus: React.FC<Props> = ({ investorId, firmId }) => {
  const { certification } = useInvestorCertification(investorId);
  const skipCoolOff = useSkipInvestmentCoolOff();
  const undoSkipCoolOff = useUndoSkipInvestmentCoolOff();
  const { data: coolOffs = [] } = useInvestmentCoolOff(investorId);
  const [canEdit] = useHasPermissions([RolePermission.EditInvestor]);

  const professionalClientCertification = (certification.data ?? []).find(
    ({ forceExpired, investorType }) =>
      !forceExpired && investorType === InvestorType.ProfessionalClient,
  );

  const handleSkipCoolOff = () => {
    skipCoolOff.mutate({ investorId, firmId });
  };

  const handleUndoSkipCoolOff = () => {
    undoSkipCoolOff.mutate({ investorId, firmId });
  };

  const isFirmCoolOffElapsed = coolOffs.find(
    (coolOff) => coolOff.firm?._id === firmId,
  )?.isElapsed;

  return (
    <>
      <RecordRow label="24-hour cool off">
        <Spacing size="sm">
          {!coolOffs.length && <span>N/A</span>}
          {coolOffs.map((coolOff) => (
            <Row spacing="sm" key={coolOff.firm?._id}>
              <div>
                {coolOff && !!professionalClientCertification ? (
                  <>
                    <span>Exempt as a professional client </span>
                    <span>
                      {dateToLabel(
                        professionalClientCertification.certificationPerformedAt,
                        undefined,
                        true,
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    {coolOff && coolOff.isElapsed && !coolOff.skippedBy && (
                      <span>Complete - </span>
                    )}
                    {coolOff && coolOff.isElapsed && !!coolOff.skippedBy && (
                      <>
                        <span>Cool off skipped - </span>
                        {!coolOff.skippedBy.isSuperAdmin && (
                          <span>approved by {coolOff.skippedBy.name} - </span>
                        )}
                      </>
                    )}
                    {coolOff && !coolOff.isElapsed && (
                      <span>Pending until </span>
                    )}
                    <span>
                      {dateToLabel(coolOff.elapsesAfter, undefined, true)}
                    </span>
                  </>
                )}
              </div>
              {coolOff &&
                coolOff.isElapsed &&
                !!coolOff.skippedBy &&
                canEdit && (
                  <>
                    <Button
                      color="primary"
                      variant="outlined"
                      size="extra-small"
                      onClick={() => handleUndoSkipCoolOff()}
                    >
                      Undo skipped cool off
                    </Button>
                  </>
                )}
            </Row>
          ))}

          {!isFirmCoolOffElapsed && canEdit && (
            <Spacing size="sm" alignItems="flex-start">
              <Button
                color="primary"
                variant="outlined"
                size="extra-small"
                onClick={() => handleSkipCoolOff()}
              >
                Skip cool off
              </Button>
            </Spacing>
          )}
        </Spacing>
      </RecordRow>
    </>
  );
};

export default CoolOffStatus;
